import Flex from "common/Boxes/Flex";
import styled, { css, useTheme } from "styled-components";
import { useQuotaTest } from "@shared/features/settings/hooks";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import Text from "common/Typography/Text";
import React, { CSSProperties } from "react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces, UsageType } from "@shared/types";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc";
import usePlanUsage from "@shared/features/settings/subscription/hooks/usePlanUsage";
import { USAGE_TYPE_TO_TITLE_OBJECT_NAME } from "@shared/features/settings/constants";
import CurrentUsageValue from "features/settings/Quota/CurrentUsageValue";
import PrimaryApiHelperTooltip from "features/settings/Billing/PrimaryApiHelperTooltip.tsx";
import { useHistory } from "react-router-dom";
import {
  IS_WEB_VIEW,
  PURCHASE_API_SLOTS_SETTINGS_PATH,
} from "common/constants";
import { USAGE_TYPE_TO_ICON } from "features/settings/common/constants.ts";
import LinkButton from "common/Buttons/LinkButton.tsx";

interface ClientUsageCardProps {
  style?: CSSProperties;
  /** New look: one quiet line ("6 of 35 API slots · Purchase"). */
  compact?: boolean;
}

const ApiSlotsUsage = ({ style, compact }: ClientUsageCardProps) => {
  const theme = useTheme();
  const { t: tPortfolio } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { testQuota, getTotalQuota } = useQuotaTest();
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t: tBilling } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const history = useHistory();
  const { user, allQuotaUsage, userUsageLimits } = usePlanUsage();

  const showApiSlotsUsage =
    isWhitelabelSubscriptionType(user?.subscription_plan_type) &&
    !user?.isExpired();

  if (!showApiSlotsUsage) return null;

  if (compact && theme.isNewLook) {
    const total = getTotalQuota(
      UsageType.CLIENT,
      userUsageLimits?.[UsageType.CLIENT]
    );
    const used = allQuotaUsage?.[UsageType.CLIENT] || 0;
    return (
      <CompactMeta style={style}>
        <CompactValue
          $over={testQuota(
            UsageType.CLIENT,
            allQuotaUsage?.[UsageType.CLIENT],
            userUsageLimits?.[UsageType.CLIENT]
          )}
        >
          {tPortfolio("create_account.new_look.api_slots", {
            used,
            total: total === "inf" ? "∞" : total,
          })}
        </CompactValue>
        <PrimaryApiHelperTooltip />
        {!IS_WEB_VIEW && (
          <>
            <span aria-hidden={true}>·</span>
            <CompactLink
              type="button"
              onClick={() => history.push(PURCHASE_API_SLOTS_SETTINGS_PATH)}
            >
              {tBilling("plan_usage.purchase")}
            </CompactLink>
          </>
        )}
      </CompactMeta>
    );
  }

  const Icon = USAGE_TYPE_TO_ICON[UsageType.CLIENT];

  return (
    <ClientUsageWrapper style={style}>
      <Flex $alignItems="center" style={{ columnGap: 4 }}>
        <Icon style={{ marginRight: 5 }} />
        <Text as="div">
          {tCommon(USAGE_TYPE_TO_TITLE_OBJECT_NAME[UsageType.CLIENT]!)}:
        </Text>
        <CurrentUsageValue
          usageType={UsageType.CLIENT}
          currentUsage={allQuotaUsage?.[UsageType.CLIENT]}
          usageLimit={userUsageLimits?.[UsageType.CLIENT]}
        />
        <PrimaryApiHelperTooltip />
      </Flex>
      {!IS_WEB_VIEW && (
        <LinkButton
          type="link"
          onClick={() => history.push(PURCHASE_API_SLOTS_SETTINGS_PATH)}
          style={{ paddingRight: 0 }}
        >
          {tBilling("plan_usage.purchase")}
        </LinkButton>
      )}
    </ClientUsageWrapper>
  );
};

export default ApiSlotsUsage;

const ClientUsageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background-color: ${(p) => p.theme.colors.statisticsBgColor};
  border-radius: 8px;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      min-height: 48px;
      padding: 6px 12px 6px 16px;
      border-radius: 12px;
      background-color: ${p.theme.colors.cardLightBgColor};
      font-size: 13px;
      svg {
        color: ${p.theme.colors.secondaryText};
      }
    `}
`;

const CompactMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  > div {
    margin-left: 0;
  }
`;
const CompactValue = styled.span<{ $over?: boolean }>`
  color: ${(p) =>
    p.$over
      ? getNewLookTokens(p.theme).down
      : getNewLookTokens(p.theme).secondary};
`;
const CompactLink = styled.button`
  padding: 0;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: none;
  font: inherit;
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).accent};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).accent};
    outline-offset: 2px;
  }
`;
